/**
 * Tiny localStorage wrapper that logs warnings (instead of silently throwing)
 * when the browser blocks access — private browsing, full disk, Safari ITP
 * partitioned origins, etc. Use this instead of bare `try/catch` blocks
 * around localStorage so failures are visible in Sentry / browser console.
 *
 * Reads return `null` on failure; writes return `false` on failure.
 */

const WARNED = new Set();

function logOnce(op, key, err) {
    const tag = `${op}:${key}`;
    if (WARNED.has(tag)) return;
    WARNED.add(tag);
    // Single warning per (op, key) — keeps the console readable.
    console.warn(`[safeStorage] ${op} blocked for "${key}":`, err?.message || err);
}

export function getItem(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (err) {
        logOnce("get", key, err);
        return null;
    }
}

export function setItem(key, value) {
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch (err) {
        logOnce("set", key, err);
        return false;
    }
}

export function removeItem(key) {
    try {
        window.localStorage.removeItem(key);
        return true;
    } catch (err) {
        logOnce("remove", key, err);
        return false;
    }
}

export function getJSON(key, fallback = null) {
    const raw = getItem(key);
    if (raw == null) return fallback;
    try {
        return JSON.parse(raw);
    } catch (err) {
        logOnce("parse", key, err);
        return fallback;
    }
}

export function setJSON(key, value) {
    try {
        return setItem(key, JSON.stringify(value));
    } catch (err) {
        logOnce("stringify", key, err);
        return false;
    }
}

// Cross-tab sync — subscribe to localStorage changes from OTHER tabs.
// The browser fires the `storage` event in every tab EXCEPT the one that
// wrote, so this lets us mirror updates without polling.
//
//   const unsub = onChange("zen_age_verified_v1", (newVal) => { ... });
export function onChange(key, handler) {
    if (typeof window === "undefined") return () => {};
    const fn = (e) => {
        if (e.storageArea !== window.localStorage) return;
        if (e.key !== key && e.key !== null) return;
        try {
            handler(e.newValue, e.oldValue);
        } catch (err) {
            logOnce("onChange-handler", key, err);
        }
    };
    window.addEventListener("storage", fn);
    return () => window.removeEventListener("storage", fn);
}
